import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { MobileBottomNav, NAV_LINK_CLASS, NAV_CTA_CLASS } from '../components/PublicNav';
import { authAxios } from '../utils/api';
import { 
  Users, Trophy, ChevronRight, UserPlus, LogIn,
  Vote, Tv, Star, Award, Target, Zap, Calendar,
  ChevronDown, FileText, HelpCircle, BookOpen, Users as UsersIcon
} from 'lucide-react';

// Color Palette:
// #1E3A4C - Dark teal/navy (primary dark)
// #FF7F50 - Coral/Orange (accent)
// #4682B4 - Steel blue (secondary)
// #F5F0E6 - Off-white/cream (background)

export default function PublicLandingPage({ API }) {
  const [stats, setStats] = useState({
    season: 50,
    episodes: 0,
    contestants: 18,
    players: 0
  });
  const [isVisible, setIsVisible] = useState({});
  const sectionRefs = useRef({});

  useEffect(() => {
    loadStats();
    
    // Set up intersection observer for scroll animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    // Observe sections
    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const loadStats = async () => {
    try {
      const [seasonsRes] = await Promise.all([
        authAxios.get(`${API}/seasons`).catch(() => ({ data: [] }))
      ]);
      
      const activeSeason = seasonsRes.data?.find(s => s.is_active);
      if (activeSeason) {
        setStats(prev => ({
          ...prev,
          season: activeSeason.name?.match(/\d+/)?.[0] || 50
        }));
      }
    } catch (error) {
      // Use defaults
    }
  };

  const setRef = (id) => (el) => {
    sectionRefs.current[id] = el;
  };

  return (
    <div className="min-h-screen overflow-hidden pb-24 md:pb-0">
      <Helmet>
        <title>Survivor Fantasy League - Draft Your Castaways & Compete!</title>
        <meta name="description" content="Join the ultimate Survivor Fantasy League! Draft your favorite CBS Survivor castaways, earn points based on real episode events, compete with friends in private leagues, and climb the leaderboard. Free to play!" />
        <link rel="canonical" href="https://survivorfantasy.live/" />
        <meta property="og:title" content="Survivor Fantasy League - Draft Your Castaways & Compete!" />
        <meta property="og:description" content="The ultimate fantasy game for Survivor fans. Draft contestants, score points from real episodes, and compete in private leagues with friends." />
        <meta property="og:url" content="https://survivorfantasy.live/" />
      </Helmet>
      
      {/* ===== HERO SECTION ===== */}
      <header className="relative min-h-screen" style={{ backgroundColor: '#F5F0E6' }}>
        {/* Background hero image - tropical sunset (placeholder; swap when ready) */}
        <div
          className="absolute inset-0"
          aria-hidden="true"
          style={{
            backgroundImage: `url("https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=2400&q=70")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        {/* Warm color wash so headline stays readable */}
        <div
          className="absolute inset-0"
          aria-hidden="true"
          style={{
            background:
              'linear-gradient(180deg, rgba(245,240,230,0.92) 0%, rgba(245,240,230,0.78) 40%, rgba(245,240,230,0.92) 100%)',
          }}
        />
        {/* Subtle dot pattern for texture */}
        <div className="absolute inset-0 opacity-10 mix-blend-multiply" style={{
          backgroundImage: `url("/sfl-bg.png")`,
          backgroundSize: '400px 400px',
          backgroundRepeat: 'repeat'
        }}></div>

        {/* Top announcement bar - hidden until Season 51 air date is announced */}

        {/* Navigation */}
        <nav className="relative z-20 container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
              <img 
                src="/logo-new.png" 
                alt="Survivor Fantasy League" 
                className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 object-contain flex-shrink-0"
              />
              <div className="min-w-0">
                <h1 className="text-[13px] sm:text-base md:text-lg lg:text-2xl font-bold text-[#1E3A4C] truncate" 
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  Survivor Fantasy League
                </h1>
                <p className="text-[#FF7F50] text-[11px] sm:text-xs lg:text-sm font-medium hidden sm:block whitespace-nowrap">Outwit • Outplay • Outlast</p>
              </div>
            </Link>
            
            <div className="flex items-center gap-1 lg:gap-3 flex-shrink-0">
              <Link to="/how-it-works">
                <Button variant="ghost" className={`hidden min-[360px]:flex ${NAV_LINK_CLASS}`}>
                  <span className="hidden sm:inline">How It Works</span>
                  <span className="sm:hidden">How To</span>
                </Button>
              </Link>

              {/* Learn More Dropdown - desktop */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className={`hidden md:flex ${NAV_LINK_CLASS}`} data-testid="nav-learn-more-trigger">
                    Learn More
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/rules" className="flex items-center gap-2 cursor-pointer" data-testid="nav-rules">
                      <FileText className="w-4 h-4" />
                      Rules & Scoring
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/faq" className="flex items-center gap-2 cursor-pointer" data-testid="nav-faq">
                      <HelpCircle className="w-4 h-4" />
                      FAQ
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/blog" className="flex items-center gap-2 cursor-pointer" data-testid="nav-blog">
                      <BookOpen className="w-4 h-4" />
                      Blog
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/about" className="flex items-center gap-2 cursor-pointer" data-testid="nav-about">
                      <UsersIcon className="w-4 h-4" />
                      About
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Link to="/login?tab=register">
                <Button variant="ghost" className={`hidden md:flex ${NAV_LINK_CLASS}`}>
                  Sign Up
                </Button>
              </Link>
              <Link to="/login">
                <Button className={NAV_CTA_CLASS} style={{ backgroundColor: '#FF7F50' }}>
                  <LogIn className="w-4 h-4 mr-1" />
                  Log In
                </Button>
              </Link>
            </div>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-4 py-16 md:py-24 lg:py-32">
          <div className="max-w-5xl mx-auto text-center">
            {/* Eyebrow */}
            <p className="text-[#FF7F50] text-sm md:text-base font-bold tracking-[0.3em] uppercase mb-6 animate-fade-in-up">
              Outwit • Outplay • Outlast
            </p>

            {/* Main Headline */}
            <h1
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-[#1E3A4C] mb-10 md:mb-12 leading-[1.05] animate-fade-in-up animation-delay-100"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Draft your tribe.{' '}
              <span className="text-[#FF7F50]">Predict the game.</span>{' '}
              Become the Sole Survivor.
            </h1>

            {/* CTA Buttons */}
            <div className="flex flex-wrap justify-center gap-4 animate-fade-in-up animation-delay-400">
              <Link to="/login">
                <Button size="lg" className="text-white px-8 py-6 text-lg rounded-full shadow-xl transform hover:scale-105 transition-all" style={{ backgroundColor: '#FF7F50' }}>
                  <UserPlus className="w-5 h-5 mr-2" />
                  Start Playing Free
                </Button>
              </Link>
              <a href="#how-it-works">
                <Button size="lg" variant="outline" className="border-2 px-8 py-6 text-lg rounded-full bg-white/80 hover:bg-white" style={{ borderColor: '#1E3A4C', color: '#1E3A4C' }}>
                  <ChevronRight className="w-5 h-5 mr-2" />
                  How It Works
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce z-20">
          <a href="#how-it-works" className="transition-colors" style={{ color: '#1E3A4C' }}>
            <ChevronRight className="w-8 h-8 rotate-90" />
          </a>
        </div>
      </header>

      {/* ===== HOW IT WORKS SECTION ===== */}
      <section 
        id="how-it-works" 
        ref={setRef('how-it-works')}
        className="py-20 relative"
        style={{ backgroundColor: '#F5F0E6' }}
      >
        <div className="container mx-auto px-4">
          <div className={`text-center mb-16 transition-all duration-700 ${isVisible['how-it-works'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h2 className="text-4xl md:text-5xl font-bold mb-4" 
                style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1E3A4C' }}>
              HOW TO PLAY
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#4682B4' }}>
              Join thousands of Survivor fans competing to see who has the best instincts. 
              It's free, it's fun, and bragging rights are on the line!
            </p>
          </div>

          {/* Steps Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: UserPlus,
                title: 'SIGN UP',
                description: 'Create your free account with email or Google. Set up your player profile in seconds.',
                color: '#4682B4',
                delay: '100'
              },
              {
                icon: Users,
                title: 'JOIN A LEAGUE',
                description: 'Create your own league or join friends. Compete head-to-head throughout the season.',
                color: '#FF7F50',
                delay: '200'
              },
              {
                icon: Trophy,
                title: 'DRAFT SURVIVORS',
                description: 'Draft your favorite castaways. Their success is your success — challenges, idols, and more!',
                color: '#1E3A4C',
                delay: '300'
              },
              {
                icon: Vote,
                title: 'MAKE PREDICTIONS',
                description: 'Answer weekly bonus questions and predict who gets voted out to earn extra points.',
                color: '#4682B4',
                delay: '400'
              },
              {
                icon: Tv,
                title: 'WATCH & SCORE',
                description: 'Watch each episode and watch your points climb as your Survivors dominate the game.',
                color: '#FF7F50',
                delay: '500'
              },
              {
                icon: Award,
                title: 'WIN THE TITLE',
                description: 'The player with the most points at finale wins! Claim the title of Fantasy Sole Survivor.',
                color: '#1E3A4C',
                delay: '600'
              },
            ].map((step, i) => (
              <div 
                key={i}
                className={`group bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-transparent hover:border-[#FF7F50] transform hover:-translate-y-2 ${
                  isVisible['how-it-works'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${step.delay}ms` }}
              >
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg"
                  style={{ backgroundColor: step.color }}
                >
                  <step.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "'Playfair Display', serif", color: '#1E3A4C' }}>
                  {step.title}
                </h3>
                <p style={{ color: '#4682B4' }}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FEATURES SECTION ===== */}
      <section 
        id="features"
        ref={setRef('features')}
        className="py-20 relative overflow-hidden"
        style={{ backgroundColor: '#1E3A4C' }}
      >
        <div className="container mx-auto px-4 relative z-10">
          <div className={`text-center mb-16 transition-all duration-700 ${isVisible['features'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" 
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              KEY FEATURES
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#F5F0E6' }}>
              Everything you need for the ultimate Survivor fantasy experience
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              {
                icon: Zap,
                title: 'Your League, Your Rules',
                description: 'Customize point values to match how your group watches the show. Reward the plays that matter most to you.',
              },
              {
                icon: Users,
                title: 'Snake Draft System',
                description: 'Fair and exciting draft format ensures everyone gets competitive picks.',
              },
              {
                icon: Star,
                title: 'Bonus Questions',
                description: 'Weekly prediction questions let you earn extra points and show off your Survivor knowledge.',
              },
              {
                icon: Trophy,
                title: 'Detailed Leaderboards',
                description: 'Track your ranking against friends and see detailed point breakdowns.',
              },
            ].map((feature, i) => (
              <div 
                key={i}
                className={`flex gap-4 items-start p-6 rounded-xl backdrop-blur-sm border transition-all duration-500 ${
                  isVisible['features'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ 
                  transitionDelay: `${i * 150}ms`,
                  backgroundColor: 'rgba(70, 130, 180, 0.2)',
                  borderColor: 'rgba(70, 130, 180, 0.3)'
                }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FF7F50' }}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                  <p style={{ color: '#F5F0E6' }}>{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SCORING RULES PREVIEW ===== */}
      <section 
        id="scoring"
        ref={setRef('scoring')}
        className="py-20 relative"
        style={{ backgroundColor: '#F5F0E6' }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `url("/sfl-bg.png")`,
          backgroundSize: '400px 400px',
          backgroundRepeat: 'repeat'
        }}></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className={`text-center mb-12 transition-all duration-700 ${isVisible['scoring'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h2 className="text-4xl md:text-5xl font-bold mb-4" 
                style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1E3A4C' }}>
              SCORING SYSTEM
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#4682B4' }}>
              Earn points for your drafted Survivors' achievements
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-4" style={{ borderColor: '#FF7F50' }}>
              {/* Header */}
              <div className="px-6 py-4" style={{ backgroundColor: '#FF7F50' }}>
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Trophy className="w-6 h-6" />
                  Point Values
                </h3>
              </div>
              
              {/* Rules List */}
              <div className="divide-y divide-gray-100">
                {[
                  { event: 'Tribe or Team Wins Challenge', points: '+3', positive: true },
                  { event: 'Individual Win', points: '+5', positive: true },
                  { event: 'Finds an Idol or Advantage', points: '+2', positive: true },
                  { event: 'Plays Idol/Advantage Successfully', points: '+5', positive: true },
                  { event: 'Correct Shot in the Dark', points: '+10', positive: true },
                  { event: 'Vote Cast for Eliminated Player', points: '+2', positive: true },
                  { event: 'Won Fire Making', points: '+7', positive: true },
                  { event: 'Final Tribal Council Appearance', points: '+15', positive: true },
                  { event: 'Wins Sole Survivor', points: '+30', positive: true },
                  { event: 'Receives Votes but Survives', points: '-2', positive: false },
                  { event: 'Voted Out at Tribal Council', points: '-5', positive: false },
                  { event: 'Quits or Medically Evacuated', points: '-10', positive: false },
                ].map((rule, i) => (
                  <div 
                    key={i} 
                    className={`flex items-center justify-between px-6 py-4 hover:bg-[#F5F0E6] transition-colors ${
                      isVisible['scoring'] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                    }`}
                    style={{ transitionDelay: `${i * 50}ms`, transitionDuration: '500ms' }}
                  >
                    <span className="font-medium" style={{ color: '#1E3A4C' }}>{rule.event}</span>
                    <span className={`text-xl font-bold ${rule.positive ? 'text-emerald-600' : 'text-red-500'}`}>
                      {rule.points}
                    </span>
                  </div>
                ))}
              </div>
              
              {/* Footer */}
              <div className="px-6 py-3 text-center" style={{ backgroundColor: '#F5F0E6' }}>
                <p className="text-sm" style={{ color: '#4682B4' }}>
                  * Points and rules may vary by league. Check with your League admin for custom rules.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA SECTION ===== */}
      <section 
        ref={setRef('cta')}
        className="py-20 relative overflow-hidden"
        style={{ backgroundColor: '#1E3A4C' }}
      >
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 rounded-full blur-3xl animate-blob" style={{ backgroundColor: '#FF7F50' }} />
          <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl animate-blob animation-delay-2000" style={{ backgroundColor: '#4682B4' }} />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="mb-8">
              <img 
                src="/logo-new.png" 
                alt="Survivor Fantasy League" 
                className="w-32 h-32 mx-auto drop-shadow-2xl"
              />
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" 
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Ready to Play?
            </h2>
            <p className="text-xl mb-10 leading-relaxed" style={{ color: '#F5F0E6' }}>
              Join the tribe! Create your free account and start competing with 
              friends and Survivor fans around the world.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/login">
                <Button size="lg" className="text-white px-10 py-7 text-xl rounded-full shadow-2xl border-2 transform hover:scale-105 transition-all" style={{ backgroundColor: '#FF7F50', borderColor: '#FF7F50' }}>
                  <UserPlus className="w-6 h-6 mr-2" />
                  Create Free Account
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="py-12" style={{ backgroundColor: '#1E3A4C' }}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="/logo-new.png" 
                alt="Survivor Fantasy League" 
                className="w-10 h-10"
              />
              <div>
                <span className="text-white font-bold text-lg">Survivor Fantasy League</span>
                <p className="text-xs" style={{ color: '#4682B4' }}>The Ultimate Survivor Experience</p>
              </div>
            </div>
            
            <div className="flex gap-6 text-sm" style={{ color: '#F5F0E6' }}>
              <a href="#how-it-works" className="hover:text-[#FF7F50] transition-colors">How to Play</a>
              <a href="#features" className="hover:text-[#FF7F50] transition-colors">Features</a>
              <a href="#scoring" className="hover:text-[#FF7F50] transition-colors">Scoring</a>
              <Link to="/login" className="hover:text-[#FF7F50] transition-colors">Login</Link>
            </div>
          </div>
          
          <div className="border-t mt-8 pt-8 text-center text-sm" style={{ borderColor: '#4682B4', color: '#4682B4' }}>
            <p>© {new Date().getFullYear()} Survivor Fantasy League. Not affiliated with CBS or Survivor.</p>
          </div>
        </div>
      </footer>

      {/* Custom CSS for animations */}
      <style jsx="true">{`
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-fade-in-up { animation: fade-in-up 0.8s ease-out forwards; }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-200 { animation-delay: 200ms; }
        .animation-delay-400 { animation-delay: 400ms; }
        .animation-delay-600 { animation-delay: 600ms; }
        .animation-delay-2000 { animation-delay: 2000ms; }
      `}</style>
      <MobileBottomNav />
    </div>
  );
}
